import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSession } from '@/lib/auth';
import { getMyProfile, getMyTier, getPointsBalance, getMySubscription } from '@/lib/api/core';

/**
 * useUserProfile — perfil del usuario autenticado.
 *
 * Fuente primaria: `GET /v1/profile/me` (cierre de GAP-02), que ya expone la
 * identidad completa (nombre, teléfono, avatar, username, documento), referral,
 * antigüedad, tier, puntos y suscripción. Si ese endpoint falla, componemos un
 * respaldo digno con `/loyalty/tiers/me` + `/loyalty/points/balance` +
 * `/subscriptions/me` y la identidad base del JWT.
 */

export type UserProfile = {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  initials: string;
  loyaltyTier: string | null;
  points: number;
  lifetimePoints: number;
  referralCode: string | null;
  memberSince: string | null;
  subscriptionActive: boolean;
  subscriptionPlan: string | null;
  /** id del plan (subscription_plans.id) — usado por el RPC activate. */
  subscriptionPlanId: string | null;
  /** 'monthly' | 'annual' — agregado en ROUND12 admin R26. */
  subscriptionCycle: 'monthly' | 'annual' | null;
  subscriptionExpiresAt: string | null;
  /** Identidad (ROUND10) — read-only en la cuenta. */
  username: string | null;
  documentType: string | null;
  documentNumber: string | null;
};

const LOYALTY_TIER_LABELS: Record<string, string> = {
  bronze: 'Bronce',
  silver: 'Plata',
  gold: 'Oro',
  platinum: 'Platino',
  diamond: 'Diamante',
};

/** Etiqueta legible en español del tier de fidelidad (`user_profiles.tier`). */
export function loyaltyTierLabel(tier: string | null | undefined): string | null {
  if (!tier || !tier.trim()) return null;
  const key = tier.trim().toLowerCase();
  return LOYALTY_TIER_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

function initialsFrom(name: string, email: string): string {
  const src = (name.trim() || email.trim()).trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

async function fetchUserProfile(userId: string, fallbackEmail: string): Promise<UserProfile> {
  // Fuente primaria: perfil enriquecido. Si falla, caemos al respaldo
  // compuesto (tier + balance + suscripción) sin romper la cuenta.
  const profile = await getMyProfile().catch(() => null);

  if (profile) {
    const email = str(profile.email) ?? fallbackEmail;
    const name = str(profile.name) ?? '';
    const points = Number(profile.points ?? 0);
    const lifetimePoints = Number(profile.lifetime_points ?? points);
    const sub = profile.subscription ?? null;
    const cycle = sub?.cycle === 'annual' || sub?.cycle === 'monthly' ? sub.cycle : null;
    return {
      id: userId,
      fullName: name || email.split('@')[0] || 'Tu cuenta',
      email,
      phone: str(profile.phone),
      avatarUrl: str(profile.avatar_url),
      initials: initialsFrom(name, email),
      // `tier` viene como nombre legible ("Bronze"); `loyaltyTierLabel` lo
      // normaliza igual que un slug.
      loyaltyTier: str(profile.tier),
      points: Number.isFinite(points) ? points : 0,
      lifetimePoints: Number.isFinite(lifetimePoints) ? lifetimePoints : 0,
      referralCode: str(profile.referral_code),
      memberSince: str(profile.member_since),
      subscriptionActive: !!sub?.active,
      subscriptionPlan: str(sub?.plan) ?? str(sub?.plan_id),
      subscriptionPlanId: str(sub?.plan_id),
      subscriptionCycle: cycle,
      subscriptionExpiresAt: str(sub?.expires_at),
      username: str(profile.username),
      documentType: str(profile.document_type),
      documentNumber: str(profile.document_number),
    };
  }

  // ---- Respaldo: composición sin /profile/me ----
  const [tier, balance, subscription] = await Promise.all([
    getMyTier().catch(() => null),
    getPointsBalance().catch(() => null),
    getMySubscription().catch(() => null),
  ]);

  const email = fallbackEmail;
  const name = '';
  const points = Number(balance?.balance ?? tier?.total_points ?? 0);
  const lifetimePoints = Number(tier?.total_points ?? points);
  const subActive = !!subscription && subscription.status === 'active';
  const cycle =
    subscription?.cycle === 'annual' || subscription?.cycle === 'monthly'
      ? subscription.cycle
      : null;

  return {
    id: userId,
    fullName: email.split('@')[0] || 'Tu cuenta',
    email,
    phone: null,
    avatarUrl: null,
    initials: initialsFrom(name, email),
    loyaltyTier: (tier?.current_tier_slug as string | null) ?? null,
    points: Number.isFinite(points) ? points : 0,
    lifetimePoints: Number.isFinite(lifetimePoints) ? lifetimePoints : 0,
    referralCode: null,
    memberSince: null,
    subscriptionActive: subActive,
    subscriptionPlan: (subscription?.plan_id as string | null) ?? null,
    subscriptionPlanId: (subscription?.plan_id as string | null) ?? null,
    subscriptionCycle: cycle,
    subscriptionExpiresAt: (subscription?.ends_at as string | null) ?? null,
    username: null,
    documentType: null,
    documentNumber: null,
  };
}

export function useUserProfile(): UseQueryResult<UserProfile> {
  const { user } = useSession();
  const userId = user?.id;

  return useQuery({
    queryKey: ['user-profile', userId ?? 'anon'],
    queryFn: () => fetchUserProfile(userId as string, user?.email ?? ''),
    enabled: !!userId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
